import React, {
  forwardRef,
  useContext,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import ShareableLinkDataContext from 'router/subrouters/ShareableLink/providers/ShareableLinkData/ShareableLinkData.context';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ResolveLogoSvg } from 'icons/Resolve.svg';
import { Button } from 'ncoded-component-library';
import ArrowrightIcon from 'icons/Arrowright.icon';
import DocumentsIcon from 'icons/Documents.icon';
import useDataRoomDesktopNavigation from 'hooks/useDataRoomDesktopNavigation';
import useNavigationLinks from 'router/subrouters/DataRoom/hooks/useNavigationLinks';
import { Link } from 'react-router-dom';
import { BUSINESS_ROUTE } from 'data/global';
import ResolveImage from 'components/ResolveImage';
import ResolveShort from 'assets/images/resolve-short.webp';
import BrandingLogo from 'components/BrandingLogo';

import './ShareableLinkDesktopNavigation.styles.scss';
import './ShareableLinkDesktopNavigation.styles.responsive.scss';

type ShareableLinkDesktopNavigationProps = {
  className?: string;
};

type ShareableLinkDesktopNavigationRef = {
  isCollapsed: boolean;
  setIsCollapsed: (val: boolean) => void;
};

const ShareableLinkDesktopNavigation: React.ForwardRefRenderFunction<
  ShareableLinkDesktopNavigationRef,
  ShareableLinkDesktopNavigationProps
> = (props, ref) => {
  const { className } = props;

  const {
    shareableLink: { name, dataRoom } = {
      name: 'Shareable link',
      dataRoom: undefined,
    },
  } = useContext(ShareableLinkDataContext);

  const { isCollapsed, shortenedName, setIsCollapsed, setIsCollapsedCallback } =
    useDataRoomDesktopNavigation(name);

  const { isWhitelabeled, logo, mobileLogo } = dataRoom || {};

  const classes = classNames(
    'shareable-link-desktop-navigation',
    { collapsed: isCollapsed },
    className,
  );

  const { t } = useTranslation();

  useImperativeHandle(
    ref,
    () => ({ isCollapsed, setIsCollapsed: setIsCollapsedCallback }),
    [isCollapsed, setIsCollapsedCallback],
  );

  const generateLinksList = useNavigationLinks();

  const MainRoutesList = useMemo(
    () =>
      generateLinksList(
        [
          {
            path: 'documents',
            textKey: 'files',
            icon: <DocumentsIcon />,
          },
        ],
        isCollapsed,
      ),
    [isCollapsed, generateLinksList],
  );

  return (
    <div className="shareable-link-desktop-navigation--collapsing-wrapper">
      <div className="shareable-link-desktop-navigation--flex-cont">
        <div className={classes}>
          <div className="shareable-link-desktop-navigation__heading">
            {!logo ? (
              <Link to={BUSINESS_ROUTE} aria-label="Resolve logo">
                {!isCollapsed ? (
                  <ResolveLogoSvg />
                ) : (
                  <ResolveImage src={ResolveShort} />
                )}
              </Link>
            ) : isCollapsed ? (
              <BrandingLogo
                brandingLogos={{ logo: mobileLogo || logo }}
                height={30}
              />
            ) : (
              <BrandingLogo brandingLogos={{ logo }} height={60} />
            )}
            <h1 style={{ textAlign: isCollapsed ? 'center' : 'left' }}>
              {!isCollapsed ? name : shortenedName}
            </h1>
          </div>

          {MainRoutesList}
        </div>

        {!isCollapsed && isWhitelabeled && (
          <div className="res-dr-powered-by">
            <p>{t('dataRoomPoweredBy')}</p>
            <ResolveLogoSvg />
          </div>
        )}

        <Button
          className={classNames(
            'shareable-link-desktop-navigation__collapse-button',
            { collapsed: isCollapsed },
          )}
          onClick={() => setIsCollapsed((old) => !old)}
        >
          <ArrowrightIcon />
          {!isCollapsed && t('collapseMenu')}
        </Button>
      </div>
    </div>
  );
};

export default forwardRef(ShareableLinkDesktopNavigation);
